/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable import/named */
/* eslint-disable camelcase */
import { pipe, path, toString, curry, map, evolve, isEmpty } from 'helpers/ramda';
import i18n from 'i18n/config';
import { IBook, ILastTrade, IPagination, Fills } from 'types/ITrade';
import type { OrderBook } from 'types/publicSocket';

import { getInt } from '../../helpers/utils';

// type pathToString = {
//   (_path: string[], obj: object): string;
//   (_path: string[]): (obj: object) => string;
// }
export const pathToString = curry((_path, obj) => pipe(path(_path), toString)(obj));

export const productIdByPayload = pathToString(['payload', 'product_id']);
export const productIdByResult = pathToString(['result', 'product_id']);

export const mapLimitPricetoPrice = ([price, size, depth]: OrderBook[number]): IBook => ({
  price: String(price),
  size: Number(size),
  depth: String(depth),
  limit_price: String(price),
});

export function calcPages(obj: IPagination): number {
  return Math.ceil(obj['x-pagination-total'] / obj['x-pagination-page-size']);
}

// type convertPriceToFixed =
//   (precision: number) => (obj: Book[]) => (Book[])

export const convertPriceToFixed = precision =>
  map(
    evolve({
      price: (_price: number | string) => {
        return parseFloat(_price.toString()).toFixed(precision);
      },
    })
  );

export function calcPrecision(data: number | string): number {
  const [, afterDecimal] = data.toString().split('.');
  return afterDecimal ? afterDecimal.length : 0;
}

/**
 * Calculates the price movement direction and other details for a trade.
 *
 * @param trade - The current trade object.
 * @param lastTrade - The previous trade object to compare against.
 * @returns An object with price movement information.
 */
export function calcPriceMovement(trade: Fills, lastTrade?: ILastTrade): any {
  let direction = 'up';
  const currentPrice = parseFloat(trade.p);
  const lastPrice = lastTrade ? parseFloat(lastTrade.price) : 0;

  if (currentPrice < lastPrice) {
    direction = 'down';
  } else if (currentPrice > lastPrice) {
    direction = 'up';
  } else {
    // Keep the same direction if price hasn't changed
    direction = lastTrade?.price_movement || 'up';
  }

  return {
    price: trade.p,
    size: trade.s,
    buyer_role: trade.r === 't' ? 'taker' : 'maker',
    timestamp: trade.t,
    price_movement: direction,
    has_changed: currentPrice !== lastPrice,
  };
}

/**
 * Processes an array of recent trades to calculate price movement information for each trade,
 * based on the previous last trade data.
 *
 * @param recent_trades - Array of trade fill objects to process.
 * @param prevlastTrade - The previous last trade object, used as a reference for the first calculation.
 * @returns An array of objects representing price movement information for each trade.
 *
 * @example
 * // Example usage:
 * const trades = [
 *   { p: 100, q: 1, t: 1715172000000 }, // p: price, q: quantity, t: timestamp (microseconds)
 *   { p: 102, q: 2, t: 1715172060000 },
 * ];
 * const prevLast = { price: 99, side: 'buy', datetime: 1715171940000 };
 * const result = populatePriceMovement(trades, prevLast);
 * // result: [
 * //   { price: 100, movement: 'up', side: 'buy', datetime: 1715172000 },
 * //   { price: 102, movement: 'up', side: 'buy', datetime: 1715172060 }
 * // ]
 */
export function populatePriceMovement(
  recent_trades: Fills[],
  prevlastTrade: ILastTrade
): ILastTrade[] {
  if (!recent_trades?.length) return [];

  return recent_trades
    .reduceRight((acc, trade) => {
      // either take lastTrade of latest update or previous lastTrade
      const lastTrade = isEmpty(prevlastTrade) ? acc[acc.length - 1] : prevlastTrade;
      return [
        ...acc,
        {
          ...calcPriceMovement(trade, lastTrade),
          datetime: Number(trade.t / 1000), // Convert microseconds to milliseconds
        },
      ];
    }, [])
    .reverse();
}

export const extractPaginationHeaders = (headers: IPagination) => ({
  currentPageNum: getInt(headers['x-pagination-page-num']),
  pageSize: getInt(headers['x-pagination-page-size']),
  totalPages: getInt(headers['x-pagination-total']),
});

// const calcTimeToProcess = (created_at: number): number => {
//   const withdrawalTime = new Date(created_at);
//   const withdrawalUTCDate = withdrawalTime.getUTCDate();
//   const withdrawalUTCHours = withdrawalTime.getUTCHours();

//   const currentTime = new Date();
//   const currentUTCDate = currentTime.getUTCDate();
//   const currentUTCHours = currentTime.getUTCHours();

//   if (currentUTCDate === withdrawalUTCDate && withdrawalUTCHours < 11) {
//     return 15 - currentUTCHours;
//   } else if (currentUTCDate === withdrawalUTCDate && withdrawalUTCHours >= 11) {
//     return 15 - currentUTCHours + 24;
//   } else if (currentUTCDate !== withdrawalUTCDate) {
//     return 15 - currentUTCHours;
//   } else {
//     return 15 - currentUTCHours;
//   }
// };

export const processWithdrawalState = withdrawal => {
  switch (withdrawal.state) {
    case 'unconfirmed':
      return {
        api_state: 'unconfirmed',
        state: i18n.t('trading:processWithdrawalState.unconfirmedState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.unconfirmedHelper'),
      };
    case 'confirmed':
      // let timeToProcess = calcTimeToProcess(withdrawal.created_at);
      // // default to 2 hrs if negative ttp
      // if (timeToProcess < 0) {
      //   timeToProcess = 2;
      // }
      return {
        // time_process: timeToProcess,
        api_state: 'confirmed',
        state: i18n.t('trading:processWithdrawalState.awaitingProcessing'),
        // state_helper_text: `Withdrawal request confirmed and will be processed within next ${timeToProcess} hours.`,
      };
    case 'cancelled':
      return {
        api_state: 'cancelled',
        state: i18n.t('trading:processWithdrawalState.cancelledState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.cancelledHelper'),
      };
    case 'initiated':
      return {
        api_state: 'initiated',
        state: i18n.t('trading:processWithdrawalState.initiatedState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.initiatedHelper'),
      };
    case 'deemed':
      return {
        api_state: 'deemed',
        state: i18n.t('trading:processWithdrawalState.initiatedState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.deemedHelper'),
      };
    case 'complete':
      return {
        api_state: 'complete',
        state: i18n.t('trading:processWithdrawalState.completeState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.completeHelper'),
      };
    default:
      return {
        api_state: '',
        state: '',
        state_helper_text: '',
      };
  }
};

export const filterDataByContractType = (contractType: string, data: any[]) => {
  return data.filter(item => item.product.contract_type.includes(contractType));
};

export const getLabelByContractType = (contractType: string, label: string) => {
  if (contractType === 'interest_rate_swaps') {
    return label.replace(i18n.t('trading:price'), i18n.t('trading:rate'));
  }
  return label;
};

export const PRIVATE_SOCKET_ACTIONS = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};
